/**
 * Identity avatars: the tint behind a face illustration or a set of initials,
 * and the generated face itself.
 *
 * Both are derived from an entity's id rather than stored, so the same persona
 * draws the same circle everywhere it appears — the personas table, the picker,
 * and every transcript that quotes it.
 *
 * This lives in `shared` because the transcript components are shared and the
 * persona module is not the only caller any more. The tints themselves are
 * design-system tokens (`--coval-avatar-N`, in `@coval/ui`'s avatar-tokens.css);
 * only the selection rule lives here.
 */

// Brand stops, not the data-viz palette: an avatar tint is chrome carrying
// identity, so nothing reads a quantity off it. Resolved through the tokens so
// the literals stay in the token layer — every consumer applies them as an
// inline backgroundColor, which takes a var() reference.
export const AVATAR_COLORS = [
  "var(--coval-avatar-1)", // blue 100
  "var(--coval-avatar-2)", // blue 200
  "var(--coval-avatar-3)", // green 100
  "var(--coval-avatar-4)", // orange 100
  "var(--coval-avatar-5)", // orange 200
  "var(--coval-avatar-6)", // purple 100
  "var(--coval-avatar-7)", // purple 200
  "var(--coval-avatar-8)", // warm neutral 200
]

/**
 * The avatar backdrop tint for an entity, derived from its id so the same one
 * always gets the same tint without storing one. With no id — a persona being
 * created — falls back to `fallbackIndex` when the caller holds a stable index,
 * and otherwise picks at random.
 */
export const getAvatarColor = (id: string, fallbackIndex?: number): string => {
  if (!id) {
    return fallbackIndex === undefined
      ? AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)]
      : AVATAR_COLORS[fallbackIndex % AVATAR_COLORS.length]
  }

  let hash = 0
  for (let index = 0; index < id.length; index++) {
    hash = id.charCodeAt(index) + ((hash << 5) - hash)
  }

  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

export interface FaceCustomization {
  hair: string
  eyes: string
  lips: string
}

/** The generated face for a seed, optionally with chosen features. */
export const getAvatarUrl = (
  seed?: string,
  options?: Partial<FaceCustomization>,
) => {
  const avatarSeed = seed || Math.random().toString(36).substring(7)
  const params = new URLSearchParams({ seed: avatarSeed })

  if (options?.hair) {
    params.append("hair", options.hair)
  }

  if (options?.eyes) {
    params.append("eyes", options.eyes)
  }

  if (options?.lips) {
    params.append("lips", options.lips)
  }

  return `https://api.dicebear.com/9.x/notionists/svg?${params.toString()}`
}
